import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { AlertOctagon, Power } from 'lucide-react';
import { getApiKey } from '../lib/api';

function apiHeaders(extra?: Record<string, string>): Record<string, string> {
  const key = getApiKey() || '';
  return { 'Content-Type': 'application/json', ...(key ? { 'X-API-Key': key } : {}), ...extra };
}

export default function KillSwitchButton() {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [halted, setHalted] = useState(false);
  const [haltTime, setHaltTime] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleHalt = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/claw/halt', {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({
          reason: 'Emergency halt - dashboard operator',
          operatorId: 'dashboard-user',
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || `Request failed (${res.status})`);
      }
      const timestamp = new Date().toLocaleTimeString();
      setHalted(true);
      setHaltTime(timestamp);
      setConfirming(false);
    } catch (err: any) {
      setError(err.message || 'Emergency halt failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-gray-900 border-[#3A352F]">
      <CardContent className="px-4 py-4">
        {/* Halted state */}
        {halted && (
          <div className="flex flex-col items-center gap-2">
            <Button
              disabled
              className="bg-gray-600 text-[#A8A29E] cursor-not-allowed w-full"
            >
              <Power aria-hidden="true" className="w-4 h-4 mr-2" />
              Fleet Halted
            </Button>
            <span className="text-xs text-[#A8A29E]">
              Fleet Halted at {haltTime}
            </span>
            <span className="text-xs text-cyan-400">
              This event has been recorded in the audit trail
            </span>
            <Button
              onClick={async () => {
                setLoading(true);
                setError(null);
                try {
                  const res = await fetch('/api/claw/resume', {
                    method: 'POST',
                    headers: apiHeaders(),
                    body: JSON.stringify({
                      operatorId: 'dashboard-user',
                      reason: 'Resume from dashboard',
                    }),
                  });
                  if (!res.ok) {
                    const body = await res.json().catch(() => null);
                    throw new Error(body?.error || `Resume failed (${res.status})`);
                  }
                  setHalted(false);
                  setHaltTime(null);
                } catch (err: any) {
                  setError(err.message || 'Fleet resume failed');
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white w-full"
            >
              <Power aria-hidden="true" className="w-4 h-4 mr-2" />
              {loading ? 'Resuming...' : 'Resume Fleet'}
            </Button>
            {error && (
              <span className="text-xs text-ag-critical">{error}</span>
            )}
          </div>
        )}

        {/* Confirmation dialog */}
        {!halted && confirming && (
          <div role="alertdialog" aria-modal="true" aria-labelledby="ksb-confirm-title" className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-ag-warning">
              <AlertOctagon aria-hidden="true" className="w-5 h-5" />
              <span id="ksb-confirm-title" className="text-sm font-medium">Confirm Emergency Halt</span>
            </div>
            <p className="text-xs text-[#A8A29E]">
              This will halt all active agents immediately. This action is logged.
              Continue?
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleHalt}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white flex-1"
              >
                {loading ? 'Halting...' : 'Confirm Halt'}
              </Button>
              <Button
                onClick={() => setConfirming(false)}
                disabled={loading}
                variant="outline"
                className="border-[#4A443D] text-[#A8A29E] hover:bg-[#292524] flex-1"
              >
                Cancel
              </Button>
            </div>
            {error && (
              <span className="text-xs text-ag-critical">{error}</span>
            )}
          </div>
        )}

        {/* Default state */}
        {!halted && !confirming && (
          <div className="flex flex-col items-center gap-2">
            <Button
              onClick={() => setConfirming(true)}
              aria-label="Emergency Halt -- stop all active agents"
              className="bg-red-600 hover:bg-red-700 text-white w-full"
            >
              <AlertOctagon aria-hidden="true" className="w-4 h-4 mr-2" />
              Emergency Halt
            </Button>
            {error && (
              <span className="text-xs text-ag-critical">{error}</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
